import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Logo from "../components/Logo";
import LandingPagePopup from "../components/LandingPagePopup";
import ChatPopupModal from "../components/ChatPopupModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Check, Star, Menu, X, Target, BookOpen, Users, Award,
  Facebook, Twitter, Linkedin, Instagram, Mail, Phone, MessageCircle, 
  Clock, Sparkles, Play, Zap, TrendingUp, GraduationCap, Brain,
  Rocket, Shield, Globe, ChevronRight, CheckCircle, ArrowUpRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Counter Animation Component
const AnimatedCounter = ({ end, duration = 2, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// Live Activity Notification
const LiveActivityFeed = () => {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const activities = [
    { name: "Sarah M.", action: "just enrolled in Web Development", location: "New York", time: "2 mins ago" },
    { name: "Raj K.", action: "completed Python Masterclass", location: "Mumbai", time: "5 mins ago" },
    { name: "Emma L.", action: "earned Career Ready badge", location: "London", time: "8 mins ago" },
    { name: "Michael C.", action: "landed a job at Google!", location: "San Francisco", time: "15 mins ago" },
    { name: "Priya S.", action: "started AI Interview Buddy", location: "Bangalore", time: "20 mins ago" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % activities.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const current = activities[currentIndex];

  return (
    <motion.div 
      className="fixed bottom-6 left-6 z-40 hidden md:block"
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: 2 }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          className="bg-white rounded-xl shadow-2xl p-4 max-w-xs border border-gray-100"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white font-bold">
              {current.name.charAt(0)}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{current.name}</p>
              <p className="text-xs text-gray-600">{current.action}</p>
              <p className="text-xs text-gray-400">{current.location} • {current.time}</p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

// Scroll Progress Bar
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 z-50 origin-left"
      style={{ scaleX: progress / 100 }}
    />
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/`);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/cms/website-settings`);
      setSettings(response.data.settings);
      setLoading(false);
    } catch (error) {
      setSettings({
        site_name: "SkillEdge",
        hero: {
          title: "Transform Your Career with AI-Powered Learning",
          subtitle: "The Future of Career Development",
          description: "Join 50,000+ professionals who accelerated their careers with personalized AI coaching, skill assessments, and expert mentorship.",
        },
        footer: { copyright_text: "© 2025 SkillEdge. All rights reserved." }
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchCourses();
  }, []);

  // Trust badge companies
  const trustBadges = [
    { name: "Google", logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" },
    { name: "Microsoft", logo: "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg" },
    { name: "Amazon", logo: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" },
    { name: "Meta", logo: "https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg" },
    { name: "Apple", logo: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" },
  ];

  // Skills for tag cloud
  const skillTags = [
    { name: "Web Development", icon: "💻", count: 150 },
    { name: "Data Science", icon: "📊", count: 89 },
    { name: "AI & Machine Learning", icon: "🤖", count: 67 },
    { name: "Product Management", icon: "📋", count: 45 },
    { name: "Digital Marketing", icon: "📱", count: 78 },
    { name: "UX Design", icon: "🎨", count: 56 },
    { name: "Cloud Computing", icon: "☁️", count: 43 },
    { name: "Cybersecurity", icon: "🔒", count: 38 },
    { name: "Business Analytics", icon: "📈", count: 52 },
    { name: "Leadership", icon: "👔", count: 34 },
  ];

  // FAQ data
  const faqs = [
    {
      question: "How does AI-powered career coaching work?",
      answer: "Our AI Career Twin analyzes your skills, experience, and goals to provide personalized career recommendations. It learns from your interactions to give increasingly accurate guidance, helping you identify skill gaps and create actionable learning paths."
    },
    {
      question: "Is SkillEdge suitable for beginners?",
      answer: "Absolutely! SkillEdge is designed for all skill levels. Our adaptive assessments identify your current level and create personalized learning paths. Whether you're a student, career changer, or experienced professional, we have resources tailored for you."
    },
    {
      question: "What makes SkillEdge different from other learning platforms?",
      answer: "Unlike traditional platforms, SkillEdge combines AI-powered career coaching, Skill DNA profiling, mock interviews with real-time feedback, and personalized career pivot planning. We focus on career outcomes, not just course completion."
    },
    {
      question: "Can I practice for job interviews?",
      answer: "Yes! Our AI Interview Buddy provides realistic interview simulations with real-time feedback on your answers, body language, and speaking pace. You can practice for specific roles and industries with questions tailored to your experience level."
    },
    {
      question: "How long does it take to see results?",
      answer: "Many users report improved confidence within the first week of using our AI coaching. Measurable skill improvements typically occur within 2-4 weeks of consistent practice. Career transitions usually take 3-12 months depending on your goals."
    },
    {
      question: "Is there a free trial available?",
      answer: "Yes! We offer a free tier that includes basic assessments, limited AI coaching sessions, and access to our career library. Premium features like unlimited AI coaching, interview practice, and skill certifications require a subscription."
    },
  ];

  // Learning journey steps
  const learningJourney = [
    { 
      step: 1, 
      title: "Discover Your Potential", 
      description: "Take our AI-powered assessment to uncover your unique skill DNA and identify growth opportunities",
      icon: <Target className="w-6 h-6" />,
      color: "from-blue-500 to-cyan-500"
    },
    { 
      step: 2, 
      title: "Get Your Personalized Plan", 
      description: "Receive a custom learning roadmap based on your goals, timeline, and current skill level",
      icon: <Brain className="w-6 h-6" />,
      color: "from-purple-500 to-pink-500"
    },
    { 
      step: 3, 
      title: "Learn & Practice", 
      description: "Access curated courses, practice with AI Interview Buddy, and build real-world projects",
      icon: <BookOpen className="w-6 h-6" />,
      color: "from-green-500 to-emerald-500"
    },
    { 
      step: 4, 
      title: "Land Your Dream Role", 
      description: "Apply confidently with polished skills, certificates, and career coaching support",
      icon: <Rocket className="w-6 h-6" />,
      color: "from-orange-500 to-red-500"
    },
  ];

  // Features grid
  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI Career Twin",
      description: "Your personal AI career advisor that learns and grows with you",
      color: "bg-blue-500"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Skill DNA Profiling",
      description: "Visualize your unique skill composition with our DNA helix analysis",
      color: "bg-purple-500"
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "AI Interview Buddy",
      description: "Practice interviews with real-time AI feedback on voice and body language",
      color: "bg-green-500"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Career Pivot Planner",
      description: "Get a week-by-week roadmap for your career transition journey",
      color: "bg-orange-500"
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: "Skill Certifications",
      description: "Earn industry-recognized certificates to showcase your expertise",
      color: "bg-pink-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Expert Mentorship",
      description: "Connect with industry professionals for personalized guidance",
      color: "bg-cyan-500"
    },
  ];

  if (loading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const { hero, footer, site_name } = settings;

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <ScrollProgress />
      <LiveActivityFeed />
      <LandingPagePopup settings={settings?.popup} onOpenChat={() => setIsChatModalOpen(true)} />
      <ChatPopupModal isOpen={isChatModalOpen} onClose={() => setIsChatModalOpen(false)} />

      {/* Navbar */}
      <motion.nav 
        className={`fixed top-0 w-full transition-all duration-300 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-lg shadow-lg' 
            : 'bg-transparent'
        }`}
        style={{ zIndex: 9999 }}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <motion.div 
              onClick={() => navigate('/')} 
              className="cursor-pointer flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-xl blur-md opacity-60"></div>
                <div className="relative w-10 h-10 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-300 to-orange-500 rounded-full animate-bounce flex items-center justify-center">
                  <Sparkles className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-extrabold tracking-tight">
                  <span className={`bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent`}>Skill</span>
                  <span className={`bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent`}>Edge</span>
                </span>
                <span className={`text-[10px] font-medium tracking-widest uppercase -mt-1 ${scrolled ? 'text-gray-500' : 'text-gray-400'}`}>
                  Learn • Grow • Excel
                </span>
              </div>
            </motion.div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className={`text-sm font-medium transition hover:text-blue-600 ${scrolled ? 'text-gray-700' : 'text-gray-600'}`}>Features</a>
              <a href="#how-it-works" className={`text-sm font-medium transition hover:text-blue-600 ${scrolled ? 'text-gray-700' : 'text-gray-600'}`}>How It Works</a>
              <a href="#skills" className={`text-sm font-medium transition hover:text-blue-600 ${scrolled ? 'text-gray-700' : 'text-gray-600'}`}>Skills</a>
              <a href="#faq" className={`text-sm font-medium transition hover:text-blue-600 ${scrolled ? 'text-gray-700' : 'text-gray-600'}`}>FAQ</a>
              <Button variant="ghost" onClick={() => navigate('/login')} className="font-medium">
                Login
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 shadow-lg"
              >
                Get Started Free
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden relative p-2 rounded-lg hover:bg-gray-100"
              style={{ zIndex: 10000 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu Overlay & Drawer - rendered via portal for z-index reliability */}
          {mobileMenuOpen && typeof document !== 'undefined' && ReactDOM.createPortal(
            <AnimatePresence mode="wait">
              <motion.div
                key="mobile-menu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Backdrop overlay */}
                <div 
                  onClick={() => setMobileMenuOpen(false)}
                  className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm"
                  style={{ zIndex: 99998 }}
                  data-testid="mobile-menu-backdrop"
                />
                {/* Drawer panel */}
                <motion.div 
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ type: 'tween', duration: 0.3 }}
                  className="md:hidden fixed top-0 right-0 h-full w-[280px] bg-white shadow-2xl overflow-y-auto"
                  style={{ zIndex: 99999 }}
                  data-testid="mobile-menu-drawer"
                >
                  {/* Drawer Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <Award className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-bold text-gray-900">SkillEdge</span>
                    </div>
                    <button 
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition"
                      data-testid="mobile-menu-close-btn"
                    >
                      <X size={20} className="text-gray-600" />
                    </button>
                  </div>
                  
                  {/* Drawer Content */}
                  <div className="p-4 space-y-2">
                    <a 
                      href="#features" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 py-3 px-4 rounded-lg transition"
                    >
                      Features
                    </a>
                    <a 
                      href="#how-it-works" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 py-3 px-4 rounded-lg transition"
                    >
                      How It Works
                    </a>
                    <a 
                      href="#skills" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 py-3 px-4 rounded-lg transition"
                    >
                      Skills
                    </a>
                    <a 
                      href="#faq" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 py-3 px-4 rounded-lg transition"
                    >
                      FAQ
                    </a>
                  </div>
                  
                  {/* Drawer Footer with Buttons */}
                  <div className="p-4 border-t border-gray-100 space-y-3 mt-auto">
                    <Button 
                      variant="outline" 
                      onClick={() => { setMobileMenuOpen(false); navigate('/login'); }} 
                      className="w-full"
                      data-testid="mobile-login-btn"
                    >
                      Login
                    </Button>
                    <Button 
                      onClick={() => { setMobileMenuOpen(false); navigate('/register'); }} 
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      data-testid="mobile-get-started-btn"
                    >
                      Get Started Free
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>,
            document.body
          )}
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-300 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-300 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-pink-200 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="space-y-8"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp}>
                <Badge className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 px-4 py-2 text-sm font-medium border-0">
                  <Sparkles className="w-4 h-4 mr-2 inline" />
                  {hero?.subtitle || "The Future of Career Development"}
                </Badge>
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight"
              >
                {hero?.title || (
                  <>
                    Transform Your Career with{' '}
                    <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                      AI-Powered Learning
                    </span>
                  </>
                )}
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-lg text-gray-600 leading-relaxed max-w-xl"
              >
                {hero?.description || "Join 50,000+ professionals who accelerated their careers with personalized AI coaching, skill assessments, and expert mentorship."}
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="px-8 py-6 text-lg border-2 hover:bg-gray-50"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Watch Demo
                </Button>
              </motion.div>

              {/* Stats Row */}
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-3">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className={`w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br ${
                        i === 1 ? 'from-blue-400 to-blue-600' :
                        i === 2 ? 'from-green-400 to-green-600' :
                        i === 3 ? 'from-purple-400 to-purple-600' :
                        'from-orange-400 to-orange-600'
                      }`}></div>
                    ))}
                  </div>
                  <div className="text-sm">
                    <span className="font-bold text-gray-900">50,000+</span>
                    <span className="text-gray-600"> students</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-900">4.9/5 rating</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image/Visual */}
            <motion.div 
              className="relative hidden lg:block"
              initial={{ opacity: 0, scale: 0.8, x: 50 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white rounded-3xl shadow-2xl p-6 transform rotate-2">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Brain className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">AI Career Coach</h3>
                      <p className="text-sm text-gray-500">Analyzing your profile...</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-3 bg-blue-100 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: '85%' }}
                        transition={{ duration: 1.5, delay: 0.5 }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">Career Readiness Score: 85%</p>
                  </div>
                </div>

                {/* Floating Badge 1 */}
                <motion.div 
                  className="absolute -top-4 -left-4 bg-white rounded-xl shadow-lg p-4"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-medium">Skill Gap Identified</span>
                  </div>
                </motion.div>

                {/* Floating Badge 2 */}
                <motion.div 
                  className="absolute -bottom-2 -right-4 bg-white rounded-xl shadow-lg p-4"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity }}
                >
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    <span className="text-sm font-medium">+25% Growth</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-center text-sm text-gray-500 mb-8">
            Trusted by professionals from world-leading companies
          </p>
          <div className="flex items-center justify-center gap-12 flex-wrap opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500">
            {trustBadges.map((badge, index) => (
              <motion.img 
                key={index}
                src={badge.logo}
                alt={badge.name}
                className="h-6 md:h-8 object-contain"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {[
              { value: 50000, suffix: "+", label: "Active Learners" },
              { value: 500, suffix: "+", label: "Expert Courses" },
              { value: 95, suffix: "%", label: "Success Rate" },
              { value: 4.9, suffix: "/5", label: "User Rating" },
            ].map((stat, index) => (
              <motion.div 
                key={index}
                variants={scaleIn}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">
                  {stat.value < 10 ? stat.value : <AnimatedCounter end={stat.value} />}{stat.suffix}
                </div>
                <div className="text-blue-100">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-0">FEATURES</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our AI-powered platform provides comprehensive tools to accelerate your career growth
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-2xl transition-shadow">
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center text-white mb-6`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works - Learning Journey */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-4 bg-green-100 text-green-700 border-0">HOW IT WORKS</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your Learning Journey
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Four simple steps to transform your career
            </p>
          </motion.div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-200 to-orange-200 -translate-y-1/2" />
            
            <motion.div 
              className="grid md:grid-cols-4 gap-8 relative"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {learningJourney.map((step, index) => (
                <motion.div 
                  key={index}
                  variants={fadeInUp}
                  className="text-center relative"
                >
                  {/* Step Number */}
                  <motion.div 
                    className={`w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white shadow-xl relative z-10`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    {step.icon}
                  </motion.div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Button 
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-10 py-6 text-lg shadow-xl"
            >
              Start Your Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Skills Tag Cloud */}
      <section id="skills" className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-4 bg-purple-100 text-purple-700 border-0">EXPLORE SKILLS</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Would You Like to Learn?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our most popular skill tracks and find the perfect path for your career goals
            </p>
          </motion.div>

          <motion.div 
            className="flex flex-wrap justify-center gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {skillTags.map((skill, index) => (
              <motion.button
                key={index}
                variants={scaleIn}
                whileHover={{ scale: 1.1, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/register')}
                className="px-6 py-3 bg-white rounded-full shadow-md hover:shadow-xl transition-all border border-gray-100 flex items-center gap-2"
              >
                <span className="text-xl">{skill.icon}</span>
                <span className="font-medium text-gray-800">{skill.name}</span>
                <Badge variant="secondary" className="ml-1 text-xs">{skill.count}+</Badge>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 max-w-4xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Career?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join 50,000+ professionals who have already accelerated their careers with SkillEdge
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-white text-blue-900 hover:bg-gray-100 px-10 py-6 text-lg shadow-xl"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg"
              >
                Contact Sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-4 bg-orange-100 text-orange-700 border-0">FAQ</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">
              Everything you need to know about SkillEdge
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl shadow-md border-0 px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <Button variant="outline" onClick={() => setIsChatModalOpen(true)}>
              <MessageCircle className="w-4 h-4 mr-2" />
              Chat with our AI Assistant
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-gray-300 py-16" data-testid="landing-footer">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-5 gap-10 mb-12">
            <div className="md:col-span-2 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white">{site_name}</span>
              </div>
              <p className="text-sm text-gray-400">
                AI-powered career development platform helping professionals achieve their career goals.
              </p>
              <div className="flex gap-4">
                {[Facebook, Twitter, Linkedin, Instagram].map((Icon, i) => (
                  <a key={i} href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-blue-600 transition">
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition">How It Works</a></li>
                <li><a href="#skills" className="hover:text-white transition">Skills</a></li>
                <li><a href="#faq" className="hover:text-white transition">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition" data-testid="footer-terms-link">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition" data-testid="footer-cookies-link">Cookie Policy</Link></li>
                <li><Link to="/refund" className="hover:text-white transition" data-testid="footer-refund-link">Refund Policy</Link></li>
                <li><Link to="/acceptable-use" className="hover:text-white transition" data-testid="footer-acceptable-use-link">Acceptable Use</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-3 text-sm">
                {/* Emails */}
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1">
                    <a href="mailto:support@skilledgeapp.com" className="hover:text-white transition">
                      support@skilledgeapp.com
                    </a>
                    <a href="mailto:info@skilledgeapp.com" className="hover:text-white transition">
                      info@skilledgeapp.com
                    </a>
                  </div>
                </li>
                {/* Phone Numbers */}
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1">
                    <div>
                      <a href="tel:+919987995359" className="hover:text-white transition">+91 9987995359</a>
                      <span className="text-gray-500 text-xs ml-1">(India, US, Europe, Asia)</span>
                    </div>
                    <div>
                      <a href="tel:+264816633437" className="hover:text-white transition">+264 816633437</a>
                      <span className="text-gray-500 text-xs ml-1">(Africa)</span>
                    </div>
                  </div>
                </li>
                {/* Website */}
                <li className="flex items-center gap-2">
                  <Globe className="w-4 h-4 shrink-0" />
                  <a href="https://www.skilledge.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">
                    www.skilledge.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              {footer?.copyright_text || "© 2025 SkillEdge. All rights reserved."}
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition">Terms</Link>
              <Link to="/cookies" className="hover:text-white transition">Cookies</Link>
              <Link to="/refund" className="hover:text-white transition">Refunds</Link>
              <Link to="/acceptable-use" className="hover:text-white transition">Acceptable Use</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* AI Chat Button */}
      <motion.button
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full shadow-2xl flex items-center justify-center z-50 hover:scale-110 transition-transform"
        onClick={() => setIsChatModalOpen(true)}
        animate={{ 
          boxShadow: [
            "0 0 0 0 rgba(99, 102, 241, 0.7)",
            "0 0 0 20px rgba(99, 102, 241, 0)",
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Sparkles className="w-7 h-7 text-white" />
      </motion.button>
    </div>
  );
};

export default LandingPage;
